/**
 * The project page's binding of the shared detail-page surface.
 *
 * Everything structural - the shell, the drag, the rail, the persistence —
 * lives in `shared/surface`. This file is the whole of what projects add:
 * its config (`./layout`) and one typed hook over the generic one.
 *
 * It binds the SELECTION-FREE hook: the project page has no detail pane, so
 * it neither names a selection kind nor publishes a `select` that would open
 * a pane it never declared. See `ProjectSurfaceContext`.
 */

import {
  useSurfaceWithoutSelection,
  UseDetailSurfaceOptions,
  UseSurfaceWithoutSelectionResult,
} from '../../shared/surface/useDetailSurface';
import { PROJECT_SURFACE, PROJECT_TABS, ProjectPanelId, ProjectTab } from './layout';

export { PROJECT_SURFACE, PROJECT_TABS };
export type { ProjectPanelId, ProjectTab };

export type ProjectWorkspace = UseSurfaceWithoutSelectionResult<
  ProjectPanelId,
  never,
  ProjectTab
>;

/**
 * The project page's layout and tab state.
 *
 * Takes the project uuid because the arrangement is remembered per project —
 * see the note at the top of `./layout`.
 */
export const useProjectWorkspace = (
  projectId: string,
  options: Omit<UseDetailSurfaceOptions<ProjectTab>, 'entityId'> = {}
): ProjectWorkspace =>
  useSurfaceWithoutSelection<ProjectPanelId, never, ProjectTab>(
    PROJECT_SURFACE,
    { ...options, entityId: projectId }
  );
